/* eslint-disable @typescript-eslint/no-shadow */
import { AllowedIcons } from 'd2/components/FaIcon/types'
import { Flexbox } from '../Layout'
import {
  filter,
  flatten,
  includes,
  isString,
} from 'lodash-es'
import {
  memo,
  useCallback,
  useMemo,
} from 'react'
import AvatarIcon from 'd2/components/AvatarIcon/AvatarIcon'
import Body from 'd2/components/Typography/Body'
import FaIcon from 'd2/components/FaIcon'
import HorizontalSpacer from 'd2/components/HorizontalSpacer'
import ReactSelect, {
  MultiValue,
  OptionProps,
  SingleValue,
  SingleValueProps,
  Theme,
  components,
} from 'react-select'
import useTheme from 'd2/hooks/useTheme'
import type {
  OptionType,
  PotentialValue,
  Props,
} from './types'

// eslint-disable-next-line react-memo/require-memo
const UnmemoizedSelect = <
  TFormValues extends AO,
  TFieldPath extends string & keyof TFormValues,
  TIsMulti extends boolean | undefined = false,
  TFieldValueType extends (TIsMulti extends true ? PotentialValue[] : PotentialValue) | null | undefined = TFormValues[TFieldPath],
>({
  autoHeight,
  borderStyling,
  controlStyling,
  error,
  inHeader = false,
  indicatorStyling,
  isClearable,
  isDisabled,
  isLoading,
  isMulti,
  isSearchable = true,
  menuStyling,
  minWidth,
  name,
  onBlur,
  onChange,
  onFocus,
  onInputChange,
  onItemSelected,
  onMenuScrollToBottom,
  optionStyling,
  options,
  placeholder,
  testID,
  value,
}: Props<TIsMulti, TFormValues, TFieldPath, TFieldValueType>) => {
  const theme = useTheme()
  const selections = useMemo(
    () => filter(
      options,
      ({ value: optionValue }) => includes(flatten<TFieldValueType>([value]), optionValue),
    ),
    [options, value],
  )

  const handleOnChange = useCallback((newSelection: MultiValue<OptionType<TFormValues, TFieldPath, TFieldValueType extends Array<any> ? TFieldValueType[0] : TFieldValueType>> | SingleValue<OptionType<TFormValues, TFieldPath, TFieldValueType extends Array<any> ? TFieldValueType[0] : TFieldValueType>>) => {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const newSelections = flatten([newSelection || []])
    const newValues = newSelections.map(({ value }) => value)
    onItemSelected?.(newSelections[0] ?? null) // only used for async select and NON-multi select
    // @ts-expect-error - TODO: properly type this
    onChange(isMulti ? newValues : newValues[0])
  }, [isMulti, onChange, onItemSelected])

  const theme2 = useCallback((provided: Theme) => ({
    ...provided,
    colors: {
      ...provided.colors,
      primary: (error ? theme.branding?.danger : theme.branding?.primaryColor) ?? provided.colors.primary!,
    },
  }), [error, theme.branding?.danger, theme.branding?.primaryColor])

  const optionComponent = (props: OptionProps<OptionType<TFormValues, TFieldPath, TFieldValueType extends Array<any> ? TFieldValueType[0] : TFieldValueType>>) => (
    <components.Option {...props}>
      <Flexbox
        align='start'
        secondaryAlign='center'
      >
        {
          isString(props.data.iconUrl)
            ? <>
              <AvatarIcon
                iconSrc={props.data.iconUrl}
                size={50}
                // TODO: Prop to disable rounding the avatar icon?
              />
              <HorizontalSpacer half />
            </>
            : isString(props.data.icon)
              ? <>
                <FaIcon
                  icon={props.data.icon}
                  weight='regular'
                />
                <HorizontalSpacer half />
              </>
              : props.data.icon
        }
        <Body>
          { props.data.label }
          { ' ' }
          <Body variant='light-italic'>
            { props.data.subText && <>
              (
              { props.data.subText }
              )
            </> }
          </Body>
        </Body>
      </Flexbox>
    </components.Option>
  )

  const singleValueComponent = (props: SingleValueProps<OptionType<TFormValues, TFieldPath, TFieldValueType extends Array<any> ? TFieldValueType[0] : TFieldValueType>>) => (
    <components.SingleValue {...props}>
      { isString(props.data.icon)
        ? <>
          <FaIcon
            // TODO: Remove 'as' type assertions because they are unsafe.
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            icon={props.data.icon as AllowedIcons}
            weight='regular'
          />
          <HorizontalSpacer half />
        </>
        : props.data.icon }
      { inHeader
        ? <Body style={{ color: theme.branding?.headerTextColor }}>
          { props.data.label }
        </Body>
        : props.data.label }
      { props.data.subText && <>
        { ' (' }
        { props.data.subText }
        )
      </> }
    </components.SingleValue>
  )

  return (
    <ReactSelect
      components={{
        Option: optionComponent,
        SingleValue: singleValueComponent,
      }}
      data-test-id={`react-select-${testID ?? ''}`}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isLoading={isLoading}
      isMulti={isMulti}
      isSearchable={isSearchable}
      name={name}
      onBlur={onBlur}
      onChange={handleOnChange}
      onFocus={onFocus}
      onInputChange={onInputChange}
      onMenuScrollToBottom={onMenuScrollToBottom}
      options={options}
      placeholder={placeholder}
      styles={{
        container: (provided) => ({
          ...provided,
          ...minWidth ? { minWidth } : {},
          fontFamily: theme.branding?.primaryFontFamily,
          height: autoHeight ? 'auto' : '2em',
        }),
        control: (provided) => ({
          ...provided,
          ...error
            ? {
              '&:hover': { borderColor: theme.colors.darkRed },
              borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
              borderColor: theme.branding?.danger,
            }
            : {},
          ...borderStyling ?? {
            borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
            borderRadius: '0px',
            borderWidth: '0px',
            boxShadow: 'none',
            minHeight: '2em',
          },
          ...isDisabled
            ? {
              backgroundColor: 'transparent', // <Box disabled sets background color and it looks weird if the background color of the select is not transparent.
            }
            : undefined,
          ...controlStyling,
        }),
        dropdownIndicator: (provided) => ({
          ...provided,
          ...error
            ? {
              '&:hover': { color: theme.colors.darkRed },
              color: theme.branding?.danger,
            }
            : {},
          ...indicatorStyling ?? {
            padding: '4px',
          },
        }),
        indicatorSeparator: () => ({
          display: 'none',
        }),
        menu: (provided) => ({
          ...provided,
          zIndex: 999,
          ...menuStyling,
        }),
        option: (provided, state) => ({
          ...provided,
          '& > span, & > i': {
            color: state.isSelected ? theme.palette.common.white : undefined,
          },
          ...state.isFocused
            ? {
              '& > i': {
                color: theme.palette.common.white,
              },
            }
            : {},
          color: state.isSelected ? theme.palette.common.white : undefined,
          ...state.isDisabled
            ? {
              color: theme.branding?.disabledText,
            }
            : {},
          ...optionStyling,
        }),
        placeholder: (provided) => ({
          ...provided,
          ...error ? { color: theme.colors.darkRed } : {},
        }),
        valueContainer: (provided) => ({
          ...provided,
          padding: 0,
        }),
      }}
      theme={theme2}
      value={isMulti ? selections : selections[0] ?? null}
    />
  )
}

UnmemoizedSelect.displayName = 'Select'

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
const Select = memo(UnmemoizedSelect) as typeof UnmemoizedSelect

Select.displayName = 'Select'

export default Select
